import { Box, Divider, Drawer, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ThemeMode } from 'config';
import useConfig from 'hooks/useConfig';
import React from 'react';
import { useSelector } from 'store';
import { closeLeftSidebar } from 'store/slices/dual-sidebar';
import MainCard from 'ui-component/cards/MainCard';
import CloseIcon from '@mui/icons-material/Close';
import { ellipsisTextOverflowStyles } from 'themes/common/typography';
import { CHAT_TRIPLEX_HEADER_OUTER_PY, LAYOUT_ZOOM_MIN_TOOLS_PANEL_PX } from './layout-zoom-constants';
import { gridSpacing } from 'store/constant';
import { SIDEBAR_DRAWER_TRANSITION_MS } from './sidebarDrawerMotion';

interface LeftAppSidebarProps {}

const LeftAppSidebar: React.FC<LeftAppSidebarProps> = () => {
  const { borderRadius } = useConfig();
  const theme = useTheme();
  const downMidXl = useMediaQuery((theme) => theme.breakpoints.down('midxl'));
  const { open, width, title, hideHeader, onClose } = useSelector(
    (state) => state.dualSidebar.left,
  );

  /** עץ בוט ופאנלים דומים — inline ליד התוכן; temporary רק במסכים צרים (overlay + z-index גבוה). */
  const useTemporaryVariant = downMidXl;

  const drawerBG = theme.palette.background.paper;
  const drawerZIndex =
    useTemporaryVariant && open ? theme.zIndex.modal : { xs: 1100, midxl: 0 };

  return (
    <Drawer
      sx={{
        width: open && !useTemporaryVariant ? width : 0,
        flexShrink: 0,
        zIndex: drawerZIndex,
        height: '100%',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.easeInOut,
          duration: SIDEBAR_DRAWER_TRANSITION_MS.enter,
        }),
        mr: 0,
        ml: 0,
        '& .MuiDrawer-paper': {
          height: '100%',
          width: width,
          minWidth: open && !useTemporaryVariant ? LAYOUT_ZOOM_MIN_TOOLS_PANEL_PX : undefined,
          transition: open
            ? theme.transitions.create(['transform', 'width'], {
                duration: SIDEBAR_DRAWER_TRANSITION_MS.enter,
                easing: theme.transitions.easing.easeInOut,
              })
            : undefined,
          boxSizing: 'border-box',
          position: 'relative',
          border: 'none',
          borderRadius: { xs: 0, midxl: `${borderRadius}px` },
          overflow: 'hidden',
        },
      }}
      variant={useTemporaryVariant ? 'temporary' : 'persistent'}
      anchor="left"
      open={open}
      ModalProps={{ keepMounted: true }}
      onClose={() => (onClose ? onClose() : closeLeftSidebar())}
      transitionDuration={SIDEBAR_DRAWER_TRANSITION_MS}
      SlideProps={{
        easing: theme.transitions.easing.easeInOut,
      }}
    >
      <MainCard
        sx={{
          bgcolor: { xs: 'transparent', [theme.breakpoints.down('midxl')]: drawerBG },
          height: '100%',
          py: 1.25,
          border: 'none',
        }}
        border={theme.palette.mode !== ThemeMode.DARK}
        content={false}
      >
        <Stack direction="column" height="100%">
          {/* Default header */}
          {!hideHeader && (
            <Box
              sx={{
                px: (t) => t.spacing(gridSpacing),
                pt: CHAT_TRIPLEX_HEADER_OUTER_PY,
              }}
            >
              <Box
                sx={{
                  pb: CHAT_TRIPLEX_HEADER_OUTER_PY,
                }}
              >
                <Grid container spacing={1.5} alignItems="center" width="100%">
                  <Grid size="grow" overflow="hidden">
                    <Typography
                      variant="h4"
                      color="text.primary"
                      fontWeight={500}
                      sx={{ ...ellipsisTextOverflowStyles }}
                    >
                      {title}
                    </Typography>
                  </Grid>
                  <Grid size="auto">
                    <IconButton onClick={() => (onClose ? onClose() : closeLeftSidebar())}>
                      <CloseIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
              <Divider />
            </Box>
          )}
          {/* Sidebar body */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                height: '100%',
                overflowX: 'hidden',
                overflowY: 'auto',
                scrollbarGutter: 'stable',
                px: 0,
              }}
            >
              <Box
                id={'left-sidebar-content-wrapper'}
                sx={{
                  paddingInlineStart: 0,
                  paddingInlineEnd: (t) => t.spacing(gridSpacing),
                  pt: 2.5,
                  pb: 0.5,
                  boxSizing: 'border-box',
                  minHeight: '100%',
                }}
              />
            </Box>
          </Box>
        </Stack>
      </MainCard>
    </Drawer>
  );
};

export default LeftAppSidebar; 